import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { Context } from '../../..';
import styles from './CertificationReview.module.scss';
import { CertificationApplication } from '../../../models/Certification';

const CertificationReview: React.FC = () => {
  const { store } = useContext(Context);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<CertificationApplication | null>(null);
  const [fileToPreview, setFileToPreview] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
  const status = watch('status', '');

  useEffect(() => {
    const loadApplication = async () => {
      if (!id) return;

      try {
        const applicationData = await store.getApplicationById(id);
        setApplication(applicationData);
        setValue('status', applicationData.status || '');
        setValue('reason', applicationData.reason || '');
        setValue('invitationDate', applicationData.invitationDate || '');
        setValue('invitationLocation', applicationData.invitationLocation || '');
        setValue('contactDetails', applicationData.contactDetails || '');
        setValue('approvalScore', applicationData.approvalScore?.toString() || '');
      } catch (error) {
        console.error('Помилка при завантаженні деталей заявки', error);
      }
    };

    loadApplication();
  }, [store, id, setValue]);

  const onSubmit = async (data: any) => {
    if (!id || !application) return;

    const updatedApplication: CertificationApplication = {
      ...application,
      status: data.status,
      reason: data.reason,
      invitationDate: data.invitationDate,
      invitationLocation: data.invitationLocation,
      contactDetails: data.contactDetails,
      approvalScore: data.approvalScore !== '' ? parseInt(data.approvalScore) : undefined,
    };

    try {
      const response = await store.updateApplicationStatus(id, updatedApplication);
      if (response.status === 200) {
        navigate('/admin/applications');
      } else {
        alert(response);
      }
    } catch (error) {
      console.error(`Помилка при оновленні статусу заявки: ${error}`);
    }
  };

  const renderResultBlock = () => {
    if (!application) return null;

    if (status === 'rejected') {
      return (
        <div className={styles['result-block']}>
          <label>Причина відхилення:</label>
          <textarea
            {...register('reason', { required: 'Причина відхилення є обов\'язковою' })}
            placeholder="Вкажіть причину відхилення"
          />
          {errors.reason && <p className={styles['error']}>{errors.reason.message}</p>}
        </div>
      );
    } else if (status === 'requested') {
      return (
        <div className={styles['result-block']}>
          <label>Дата запрошення:</label>
          <input
            type="date"
            {...register('invitationDate', { required: 'Дата запрошення є обов\'язковою' })}
          />
          {errors.invitationDate && <p className={styles['error']}>{errors.invitationDate.message}</p>}
          <label>Місце запрошення:</label>
          <input
            type="text"
            {...register('invitationLocation', { required: 'Місце запрошення є обов\'язковим' })}
            placeholder="Вкажіть місце запрошення"
          />
          {errors.invitationLocation && <p className={styles['error']}>{errors.invitationLocation.message}</p>}
          <label>Контактні дані:</label>
          <input
            type="text"
            {...register('contactDetails', { required: 'Контактні дані є обов\'язковими' })}
            placeholder="Вкажіть контактні дані"
          />
          {errors.contactDetails && <p className={styles['error']}>{errors.contactDetails.message}</p>}
        </div>
      );
    } else if (status === 'active') {
      return (
        <div className={styles['result-block']}>
          <label>Оцінка:</label>
          <input
            type="number"
            {...register('approvalScore', { required: 'Оцінка є обов\'язковою' })}
            placeholder="Вкажіть оцінку"
          />
          {errors.approvalScore && <p className={styles['error']}>{errors.approvalScore.message}</p>}
        </div>
      );
    }
    return null;
  };

  const handleFileClick = (url: string) => {
    setFileToPreview(`${process.env.REACT_APP_BASE_URL}/${url}`);
  };

  if (!application) {
    return <div>Завантаження...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['admin-review-form']}>
      <h1>Підтвердження сертифікації для: {application.certification.name}</h1>
      <p className={styles['applications__item-fullname']}>ПІБ: {application.full_name}</p>
      <div>
        {application.files.map((file, index) => (
          <div key={index} className={styles['file-preview']}>
            <label>{file.name}</label>
            <button type='button' onClick={() => handleFileClick(file.url)} className={styles['file-button']}>
              Переглянути файл
            </button>
          </div>
        ))}
      </div>
      {application.additional_files && (
        <div className={styles['file-preview']}>
          <label>Додаткові файли:</label>
          {application.additional_files.slice(0, 2).map((file, index) => (
            <div key={index}>
              <button type='button' onClick={() => handleFileClick(file.url)} className={styles['file-button']}>
                Переглянути файл {index + 1}
              </button>
            </div>
          ))}
        </div>
      )}
      {fileToPreview && (
        <div className={styles['file-viewer']}>
          <iframe title='file' src={fileToPreview} width="100%" height="600px"></iframe>
        </div>
      )}
      <div className={styles['status-select']}>
        <label>Статус:</label>
        <select {...register('status')} defaultValue={status} onChange={(e) => setValue('status', e.target.value)}>
          <option value="pending">Очікує підтвердження</option>
          <option value="active">Активний</option>
          <option value="requested">Запрошено на екзамен</option>
          <option value="rejected">Відмовлено</option>
        </select>
      </div>
      {renderResultBlock()}
      <div className={styles['buttons-container']}>
        <button type="submit" className={styles['save-button']}>Зберегти</button>
        <button onClick={() => navigate('/admin/applications')} className={styles['back-button']} type="button">Повернутись до списку</button>
      </div>
    </form>
  );
};

export default observer(CertificationReview);
